import React, { useState, useRef, useEffect } from "react";
import { useI18n } from "@/i18n";
import { API } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Sparkles, Coffee } from "lucide-react";
import ReactMarkdown from "react-markdown";

// Stable Markdown component map (avoids re-creating on every render)
const MD_COMPONENTS = {
  p: ({ children }) => <p className="my-1 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-[#C86240]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc ms-5 my-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ms-5 my-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  code: ({ children }) => <code className="px-1.5 py-0.5 rounded bg-[#2A2421]/8 text-[#C86240] text-xs">{children}</code>,
  h1: ({ children }) => <h3 className="font-serif-en text-lg mt-2 mb-1">{children}</h3>,
  h2: ({ children }) => <h3 className="font-serif-en text-lg mt-2 mb-1">{children}</h3>,
  h3: ({ children }) => <h3 className="font-serif-en text-base mt-2 mb-1">{children}</h3>,
};

function MessageBody({ message, isLast, streaming, thinkingLabel }) {
  if (message.role === "assistant" && message.content) {
    return <ReactMarkdown components={MD_COMPONENTS}>{message.content}</ReactMarkdown>;
  }
  if (message.content) {
    return message.content;
  }
  if (streaming && isLast) {
    return <span className="opacity-50">{thinkingLabel}</span>;
  }
  return null;
}

export default function Assistant() {
  const { t, lang, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const [messages, setMessages] = useState([
    { role: "assistant", content: t.assistant.welcome },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(`session_${Date.now()}`);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId.current, lang }),
      });
      if (!res.body) throw new Error("No stream body");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          if (payload.startsWith("[ERROR]")) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: payload };
              return copy;
            });
            continue;
          }
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = {
              role: "assistant",
              content: copy[copy.length - 1].content + payload,
            };
            return copy;
          });
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: String(err) };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="assistant-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-[#C86240]/10 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-[#C86240]" />
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#C86240]">{isRTL ? "ذكاء" : "AI"}</div>
      </div>
      <h1 className={`${serifClass} text-4xl lg:text-6xl text-[#2A2421] leading-[1.02]`}>{t.assistant.title}</h1>
      <p className="mt-4 text-[#5C544F] leading-relaxed max-w-2xl">{t.assistant.lead}</p>

      <div className="mt-10 soft-card rounded-2xl p-6 md:p-8" data-testid="chat-container">
        <div className="space-y-5 min-h-[300px] max-h-[55vh] overflow-y-auto pe-1">
          {messages.map((m, i) => (
            <div key={`msg-${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`message-${m.role}-${i}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-5 py-3.5 leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#2A2421] text-[#F9F6F0]"
                    : "bg-[#F9F6F0] border border-[#2A2421]/10 text-[#2A2421]"
                }`}
              >
                {m.role === "assistant" && (
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#C86240] mb-1">
                    <Coffee className="w-3 h-3" /> {t.assistant.title}
                  </div>
                )}
                <div className="whitespace-pre-wrap text-sm prose-sm max-w-none">
                  <MessageBody
                    message={m}
                    isLast={i === messages.length - 1}
                    streaming={streaming}
                    thinkingLabel={t.assistant.thinking}
                  />
                </div>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="mt-6 flex gap-3" data-testid="chat-form">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t.assistant.placeholder}
            disabled={streaming}
            data-testid="chat-input"
            className="h-12 rounded-xl border-[#2A2421]/15 bg-[#F9F6F0]"
          />
          <Button
            type="submit"
            disabled={streaming || !input.trim()}
            data-testid="chat-send-btn"
            className="h-12 px-6 rounded-xl bg-[#C86240] hover:bg-[#b15637] text-white"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
